import theme from "assets/theme";
import { attachSignedFileUrls, uploadFileWeb } from "helpers/shared.helpers";
import { React, useCallback, useContext, useEffect, useState } from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";
import OrganizationService from "services/Organization.service";
import useUnsavedChangesGuard from "web/hooks/useUnsavedChangesGuard";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import ColorPicker from "webComponentsFormElements/inputs/ColorPicker.component";
import CustomButton from "webComponentsFormElements/inputs/CustomButton.component";
import FileUpload from "webComponentsFormElements/inputs/FileUpload.component";
import LabelInput from "webComponentsFormElements/inputs/LabelInput.component";
import { AuthContext } from "webContext/Auth.context";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";

const classes = StyleSheet.create({
  root: {
    gap: 15,
    overflow: "auto",
  },
  settingsSectionsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 30,
    overflow: "auto",
  },
  header: {
    color: theme.color.darkGrey,
    fontWeight: theme.font.weight.semibold,
    fontSize: 16,
    marginBottom: 8,
  },
  requestModalContent: {
    gap: 15,
    padding: 20,
    minWidth: 400,
  },
  disabledField: {
    opacity: 0.6,
  },
  requestButtonsContainer: {
    gap: 10,
    marginTop: 10,
    display: "flex",
    flexDirection: "row",
  },
});

const DEFAULT_ORGANIZATION = {
  name: "",
  nameShort: "",
  colorScheme: "#1554A3",
};

export default function OrganizationSettingsScreen() {
  const { organization, getOrganization } = useContext(OrganizationContext);
  const { showToast } = useContext(ToastContext);
  const { isAdmin } = useContext(AuthContext);

  const [isLoading, setIsLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [newOrganization, setNewOrganization] = useState(DEFAULT_ORGANIZATION);
  const [newOrganizationImage, setNewOrganizationImage] = useState(null);
  const [newOrganizationImageBlob, setNewOrganizationImageBlob] =
    useState(null);

  const [showNameChangeRequest, setShowNameChangeRequest] = useState(false);
  const [showDeleteRequest, setShowDeleteRequest] = useState(false);

  const [requestedName, setRequestedName] = useState("");
  const [requestedNameShort, setRequestedNameShort] = useState("");
  const [deleteReason, setDeleteReason] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await getOrganization();
      setIsLoading(false);
    };
    fetchData();
  }, [getOrganization]);

  useEffect(() => {
    setNewOrganization(organization || DEFAULT_ORGANIZATION);
    setNewOrganizationImage(organization?.signedFileUrl);
    setNewOrganizationImageBlob(null);
    setRequestedName(organization?.name || "");
    setRequestedNameShort(organization?.nameShort || "");
  }, [organization]);

  const handleNameChangeRequest = useCallback(async () => {
    try {
      await OrganizationService.requestOrganizationNameChange({
        newName: requestedName,
        newNameShort: requestedNameShort,
        currentName: organization?.name,
        currentNameShort: organization?.nameShort,
      });

      setShowNameChangeRequest(false);
      showToast("Name change request submitted successfully.", "success");
    } catch (error) {
      showToast("Failed to submit name change request.", "error");
      if (__DEV__) {
        console.error("Error submitting name change request:", error);
      }
    }
  }, [requestedName, requestedNameShort, organization, showToast]);

  const handleDeleteRequest = useCallback(async () => {
    try {
      await OrganizationService.requestOrganizationDeletion({
        reason: deleteReason,
        organizationName: organization?.name,
      });

      setShowDeleteRequest(false);
      setDeleteReason("");
      showToast("Deletion request submitted successfully.", "success");
    } catch (error) {
      showToast("Failed to submit deletion request.", "error");
      if (__DEV__) {
        console.error("Error submitting deletion request:", error);
      }
    }
  }, [deleteReason, organization, showToast]);

  const onSave = useCallback(async () => {
    try {
      let fileId =
        newOrganizationImage === newOrganization.signedFileUrl
          ? newOrganization.fileId
          : null;
      if (newOrganizationImageBlob) {
        const res = await uploadFileWeb(newOrganizationImageBlob);
        fileId = res.fileId;
      }

      const { colorScheme } = newOrganization;

      const organizationRes = await OrganizationService.updateOrganization({
        fileId,
        colorScheme,
      });

      const updatedOrganizationRes = await attachSignedFileUrls(
        organizationRes.payload,
      );

      await getOrganization();

      setNewOrganization(updatedOrganizationRes);

      setHasUnsavedChanges(false);

      showToast("Successfully saved organization settings.", "success");
    } catch (error) {
      showToast("Failed to save organization settings.", "error");
      if (__DEV__) {
        console.error("Error saving organization settings:", error);
      }
    }
  }, [
    getOrganization,
    newOrganization,
    newOrganizationImage,
    newOrganizationImageBlob,
    showToast,
  ]);

  // Use modular hook
  useUnsavedChangesGuard(hasUnsavedChanges, "__orgSettingsUnsavedChanges");

  if (isLoading) {
    return <ActivityIndicator size="large" />;
  }

  return (
    <View style={classes.root}>
      <View style={classes.settingsSectionsContainer}>
        <View>
          <FileUpload
            file={newOrganizationImage}
            disabled={!isAdmin}
            setFile={(image) => {
              setNewOrganizationImage(image);
              setHasUnsavedChanges(true);
            }}
            setFileBlob={setNewOrganizationImageBlob}
          />
          <View>
            <View style={classes.disabledField}>
              <LabelInput
                label="Name"
                disabled={true}
                value={organization?.name || ""}
                onChange={() => {}}
                required
              />
            </View>
            <View style={classes.disabledField}>
              <LabelInput
                label="Short Name"
                disabled={true}
                value={organization?.nameShort || ""}
                onChange={() => {}}
              />
            </View>
            {isAdmin && (
              <View style={classes.requestButtonsContainer}>
                <CustomButton
                  title="Request to change name of organization"
                  variant="light"
                  icon="pencil"
                  onPress={() => setShowNameChangeRequest(true)}
                />
                <CustomButton
                  title="Request to delete organization"
                  icon="delete-forever"
                  variant="danger"
                  onPress={() => setShowDeleteRequest(true)}
                />
              </View>
            )}
          </View>
        </View>
        <View>
          <ColorPicker
            label="Color Scheme"
            disabled={!isAdmin}
            selectedColor={newOrganization.colorScheme}
            setSelectedColor={(colorScheme) => {
              setNewOrganization((oldOrganizationInfo) => ({
                ...oldOrganizationInfo,
                colorScheme,
              }));
              setHasUnsavedChanges(true);
            }}
          />
        </View>
      </View>

      <CustomModal
        open={showNameChangeRequest}
        onClose={() => {
          setShowNameChangeRequest(false);
          setRequestedName(organization?.name || "");
          setRequestedNameShort(organization?.nameShort || "");
        }}
        title="Request Organization Name Change"
        additionalButtons={{
          after: [
            {
              title: "Submit Request",
              variant: "dark",
              onPress: handleNameChangeRequest,
              disabled: !requestedName.trim(),
            },
          ],
        }}
      >
        <View style={classes.requestModalContent}>
          <Text>
            Please provide the new organization name and abbreviation
            (optional):
          </Text>
          <LabelInput
            label="New Organization Name"
            value={requestedName}
            onChange={(event) => setRequestedName(event.target.value)}
            required
          />
          <LabelInput
            label="New Organization Short Name (Optional)"
            value={requestedNameShort}
            onChange={(event) => setRequestedNameShort(event.target.value)}
          />
        </View>
      </CustomModal>

      <CustomModal
        open={showDeleteRequest}
        onClose={() => {
          setShowDeleteRequest(false);
          setDeleteReason("");
        }}
        title="Request Organization Deletion"
        additionalButtons={{
          after: [
            {
              title: "Submit Request",
              variant: "danger",
              onPress: handleDeleteRequest,
              disabled: !deleteReason.trim(),
            },
          ],
        }}
      >
        <View style={classes.requestModalContent}>
          <Text>
            Please provide a reason why the organization must be deleted:
          </Text>
          <LabelInput
            label="Reason for deletion (max 200 characters)"
            value={deleteReason}
            onChange={(event) => {
              const value = event.target.value;
              if (value.length <= 200) {
                setDeleteReason(value);
              }
            }}
            multiline
            rows={4}
            required
          />
          <Text style={{ fontSize: 12, color: theme.color.mediumGrey }}>
            {deleteReason.length}/200 characters
          </Text>
        </View>
      </CustomModal>

      <View style={{ flexDirection: "row", gap: 10 }}>
        <CustomButton
          title="Save changes"
          icon="content-save-outline"
          variant="dark"
          disabled={!hasUnsavedChanges}
          onPress={onSave}
        />
      </View>
    </View>
  );
}
